import { useCallback, useEffect, useState } from 'react'
import Icon, { type IconName } from '../components/Icon'
import { imageUrl, worx, type Membership } from './client'
import { Standing } from './Business'
import { Logo, initials } from './Jobs'
import { tw } from './strings'

type Data = Awaited<ReturnType<typeof worx.dashboard>>

/**
 * The employer's home screen.
 *
 * ── What the three tiles actually count ────────────────────────────────────
 *
 * "New applicants" is applications nobody has OPENED, not applications that
 * arrived today. A dashboard number has to mean "this still needs you", or it
 * resets overnight while the work is still sitting there untouched.
 *
 * "Unread messages" is per person, not per business: two managers on one
 * restaurant have different inboxes, and a shared count would clear itself
 * when the other one read something.
 *
 * ── Why the tiles are not buttons ──────────────────────────────────────────
 *
 * Two of them are, and one is not. Active jobs and new applicants have an
 * obvious destination; unread messages does too. A count with nowhere to go
 * would be decoration, so anything that cannot be tapped does not look like it
 * can.
 */
export default function Dashboard({
  membership,
  onPostJob,
  onApplicants,
  onGoJobs,
  onGoMessages,
  onReviewBusiness,
}: {
  membership: Membership
  onPostJob: () => void
  onApplicants: (jobId: string) => void
  onGoJobs: () => void
  onGoMessages: () => void
  onReviewBusiness?: () => void
}) {
  const [data, setData] = useState<Data | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await worx.dashboard(membership.employerId))
    } catch {
      setData(null)
    }
  }, [membership.employerId])

  useEffect(() => { void load() }, [load])

  if (!data) return <p className="muted">…</p>

  const { counts } = data

  return (
    <div className="w-dash">
      <h1>{tw('w.dash.heading')}</h1>

      <Standing membership={membership} onReviewBusiness={onReviewBusiness} />

      <button className="primary w-post" onClick={onPostJob}>
        <Icon name="plus" size={20} /> {tw('w.dash.post')}
      </button>

      <div className="w-tiles">
        <Tile
          icon="briefcase" tone="mint" n={counts.activeJobs}
          label={tw('w.dash.activeJobs')} onClick={onGoJobs}
        />
        <Tile
          icon="people" tone="amber" n={counts.newApplicants}
          label={tw('w.dash.newApplicants')}
          // Straight to the posting with unreviewed applications if there is
          // exactly one; otherwise to the list, where they can choose.
          onClick={() => {
            const waiting = data.jobs.filter((j) => j.unreviewed > 0)
            if (waiting.length === 1) onApplicants(waiting[0]!.id)
            else onGoJobs()
          }}
        />
        <Tile
          icon="chat" tone="coral" n={counts.unreadMessages}
          label={tw('w.dash.unread')} onClick={onGoMessages}
        />
      </div>

      <section className="w-dash-sec">
        <header className="w-dash-head">
          <h2>{tw('w.dash.openJobs')}</h2>
          <button className="link" onClick={onGoJobs}>
            {tw('w.dash.viewAllJobs')} <Icon name="chevron" size={16} />
          </button>
        </header>

        {!data.jobs.length && <p className="muted w-empty">{tw('w.post.none')}</p>}

        {data.jobs.map((job) => (
          <article key={job.id} className="w-job-row">
            <Logo name={membership.name} src={data.employer.logoId} />
            <div className="w-job-row-body">
              <div className="w-job-row-top">
                <h3>{job.title}</h3>
                <span className={`w-pill w-pill-${job.status === 'published' ? 'hired' : 'paused'}`}>
                  {job.status === 'published' ? tw('w.dash.active') : tw('w.post.pause')}
                </span>
              </div>
              <p className="w-meta"><Icon name="people" size={15} /> {job.applicants} {tw('w.dash.applicants')}</p>
              <p className="w-meta"><Icon name="calendar" size={15} /> {posted(job.publishedAt)}</p>
              <button className="w-teal w-job-row-cta" onClick={() => onApplicants(job.id)}>
                {tw('w.dash.viewApplicants')} <Icon name="chevron" size={16} />
              </button>
            </div>
          </article>
        ))}
      </section>

      <section className="w-dash-sec">
        <header className="w-dash-head">
          <h2>{tw('w.dash.recent')}</h2>
        </header>

        {!data.applicants.length && <p className="muted w-empty">{tw('w.applicants.none')}</p>}

        <ul className="w-people">
          {data.applicants.map((a) => (
            <li key={a.id}>
              <button onClick={() => onApplicants(a.jobId)}>
                {/* Their photo if they have one; their initials if not. A
                    column of grey silhouettes would be worse than either. */}
                {imageUrl(a.photoId)
                  ? <img className="w-avatar" src={imageUrl(a.photoId)!} alt="" width={42} height={42} loading="lazy" decoding="async" />
                  : <span className="w-avatar" aria-hidden="true">{initials(a.name)}</span>}
                <span className="w-person">
                  <span className="w-person-name">{a.name}</span>
                  <span className="w-person-sub">{a.jobTitle}</span>
                </span>
                <span className={`w-pill w-pill-${a.viewedAt ? a.status : 'new'}`}>
                  {a.viewedAt ? tw(`w.status.${a.status}` as never) : tw('w.dash.new')}
                </span>
                <Icon name="chevron" size={18} className="w-person-go" />
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

function Tile({
  icon, tone, n, label, onClick,
}: {
  icon: IconName
  tone: 'mint' | 'amber' | 'coral'
  n: number
  label: string
  onClick: () => void
}) {
  return (
    <button className={`w-tile w-tile-${tone}`} onClick={onClick}>
      <span className="w-tile-icon"><Icon name={icon} size={20} /></span>
      <span className="w-tile-n">{n}</span>
      <span className="w-tile-label">{label}</span>
    </button>
  )
}

/**
 * "Posted 2 days ago", in the reader's language.
 *
 * `Intl.RelativeTimeFormat` rather than a hand-written ladder: it is built in,
 * it knows that Spanish says "hace 2 días", and a hand-written one would be a
 * second place for the two languages to drift.
 */
function posted(at: number | null): string {
  if (!at) return tw('w.dash.notPosted')
  const days = Math.round((at - Date.now()) / 86_400_000)
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  return `${tw('w.dash.posted')} ${rtf.format(days, 'day')}`
}
